import { useContext, useState, useTransition } from 'react';
import { Collection, OverlayTriggerStateContext } from 'react-aria-components';
import { pluralize } from '@gonfalon/format/pluralize';
import { Autocomplete } from '@gonfalon/launchpad-experimental/Autocomplete';
import { SearchField } from '@gonfalon/launchpad-experimental/Form';
import { TextTruncator } from '@gonfalon/launchpad-experimental/TextTruncator';
import { MemberAvatar } from '@gonfalon/members/MemberAvatar';
import { Dialog, Header, ListBox, ListBoxItem, ListBoxSection, Separator, Text } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import type { MemberItem, MultipleMaintainerProps, SingleMaintainerProps, TeamItem } from './types';
import type { UseMaintainerItemsParams } from './useMaintainerItems';
import { useMaintainerItems } from './useMaintainerItems';
import { useMaintainerSelection } from './useMaintainerSelection';
import { getSelectedValues } from './utils';

import styles from './MaintainerDialog.module.css';

export interface CommonProps extends Pick<
  UseMaintainerItemsParams,
  'teamsOnly' | 'membersOnly' | 'accessChecks' | 'filterOutCurrentMember' | 'maxTeams' | 'maxMembers'
> {
  disableSelection?: boolean;
  closeOnSelection?: boolean;
  clearSelectionLabel?: string;
  onClearSelection?: () => void;
  /** Optional leading list item for filtering to resources with no maintainer. */
  noMaintainerOption?: { isSelected: boolean; onSelect: () => void };
}

type MaintainerDialogProps = (SingleMaintainerProps | MultipleMaintainerProps) & CommonProps;

export function MaintainerDialog(props: MaintainerDialogProps) {
  const {
    selectionMode,
    teamsOnly,
    membersOnly,
    disableSelection,
    closeOnSelection,
    clearSelectionLabel,
    onClearSelection,
    noMaintainerOption,
    accessChecks,
    filterOutCurrentMember,
    maxTeams,
    maxMembers,
  } = props;
  const overlayState = useContext(OverlayTriggerStateContext);
  const [isPending, startTransition] = useTransition();
  const [filterText, setFilterText] = useState('');
  const label = selectionMode === 'single' ? 'Choose maintainer' : 'Choose maintainers';

  const handleFilterTextChange = (value: string) => {
    startTransition(() => {
      setFilterText(value);
    });
  };

  const { maintainerId, maintainerTeamKey, maintainerIds, maintainerTeamKeys } = getSelectedValues(props);

  const { itemsInSections } = useMaintainerItems({
    maintainerId,
    maintainerIds,
    maintainerTeamKey,
    maintainerTeamKeys,
    filterText,
    teamsOnly,
    membersOnly,
    accessChecks,
    filterOutCurrentMember,
    maxTeams,
    maxMembers,
    suspense: true,
  });

  const { handleSelectionChange, isPending: isPendingSelection } = useMaintainerSelection({
    closeOnSelection,
    itemsInSections,
    ...props,
  });

  const getSelectedKeys = () => {
    if (selectionMode === 'multiple') {
      return [...maintainerIds, ...maintainerTeamKeys];
    }

    // single selection mode
    if (maintainerId !== undefined) {
      return [maintainerId];
    }

    if (maintainerTeamKey !== undefined) {
      return [maintainerTeamKey];
    }

    return [];
  };

  const selectedKeys = getSelectedKeys();
  const canClearSelection =
    selectionMode === 'single' && selectedKeys.length > 0 && clearSelectionLabel && onClearSelection;
  const selectedSection = canClearSelection ? itemsInSections.find((section) => section.id === 'selected') : undefined;
  const hasSelectedSection = canClearSelection && selectedSection !== undefined;
  const shouldShowSelectedControls = hasSelectedSection && filterText === '';
  const visibleSections = hasSelectedSection
    ? itemsInSections.filter((section) => section.id !== 'selected')
    : itemsInSections;
  const renderSection = (section: MemberItem | TeamItem) =>
    section.type === 'team' ? (
      <TeamListItem key={section.id} {...section} disabled={disableSelection} />
    ) : section.type === 'member' ? (
      <MemberListItem key={section.id} {...section} disabled={disableSelection} />
    ) : null;

  return (
    <Dialog aria-label={label} data-test-id="maintainer-dialog">
      <Autocomplete>
        <SearchField
          autoFocus
          aria-label="Filter items"
          isBusy={isPending || isPendingSelection}
          onChange={handleFilterTextChange}
        />
        {shouldShowSelectedControls && (
          <ListBox
            selectionMode={selectionMode}
            onSelectionChange={handleSelectionChange}
            selectedKeys={selectedKeys}
            items={[selectedSection]}
          >
            {(section) => (
              <ListBoxSection id={section.id}>
                <Header>{section.name}</Header>
                <Collection items={section.children}>{renderSection}</Collection>
              </ListBoxSection>
            )}
          </ListBox>
        )}
        {shouldShowSelectedControls && (
          <div className={styles.clearAction}>
            <button type="button" className={styles.clearActionButton} onClick={onClearSelection}>
              <span className={styles.clearActionLabel}>
                <Icon name="cancel-circle-outline" size="small" />
                <TextTruncator>{clearSelectionLabel}</TextTruncator>
              </span>
            </button>
          </div>
        )}
        <ListBox
          selectionMode={selectionMode}
          onSelectionChange={handleSelectionChange}
          selectedKeys={selectedKeys}
          items={visibleSections}
        >
          {(section) => (
            <ListBoxSection id={section.id}>
              <Header>{section.name}</Header>
              <Collection items={section.children}>{renderSection}</Collection>
            </ListBoxSection>
          )}
        </ListBox>
        {/* "No maintainer" filter option, after the member/team lists. Hidden while searching,
            since it isn't a searchable member/team and shouldn't sit above live results. */}
        {noMaintainerOption && filterText === '' && (
          <>
            <Separator />
            <ListBox
              aria-label="Other maintainer filters"
              selectionMode="single"
              selectedKeys={noMaintainerOption.isSelected ? ['__no_maintainer__'] : []}
              onSelectionChange={() => {
                noMaintainerOption.onSelect();
                if (closeOnSelection && overlayState) {
                  overlayState.close();
                }
              }}
            >
              <ListBoxItem id="__no_maintainer__" textValue="No maintainer">
                <Text slot="label">
                  <Icon name="person-off" size="small" />
                  <TextTruncator>No maintainer</TextTruncator>
                </Text>
              </ListBoxItem>
            </ListBox>
          </>
        )}
      </Autocomplete>
    </Dialog>
  );
}

export function MemberListItem(props: Omit<MemberItem, 'type'>) {
  return (
    <ListBoxItem id={props.id} textValue={props.id} isDisabled={props.disabled}>
      <Text slot="label">
        <MemberAvatar size="small" email={props.email} initials={props.initials} />
        <TextTruncator>{props.name}</TextTruncator>
      </Text>
      {props.description && (
        <Text slot="description">
          <TextTruncator>{props.description}</TextTruncator>
        </Text>
      )}
    </ListBoxItem>
  );
}

export function TeamListItem(
  props: Omit<TeamItem, 'type'> & {
    includeTeamCount?: boolean;
  },
) {
  const { id, name, disabled, description, teamCount = 0, includeTeamCount } = props;
  return (
    <ListBoxItem id={id} textValue={id} isDisabled={disabled}>
      <Text slot="label">
        <Icon name="group" size="small" />
        <TextTruncator>{name}</TextTruncator>
        {includeTeamCount && teamCount > 0 && (
          <Text size="small">
            ({teamCount} {pluralize(teamCount, 'person', 'people')})
          </Text>
        )}
      </Text>
      {description && (
        <Text slot="description">
          <TextTruncator>{description}</TextTruncator>
        </Text>
      )}
    </ListBoxItem>
  );
}
